import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  NgZone,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  asyncScheduler, catchError, EMPTY, filter, fromEvent, interval, merge, Observable, of, switchMap, take, tap, throttleTime,
} from 'rxjs';

import { ROLES } from 'src/app/app-const';
import { SupportManualService } from 'src/app/core/backend/academy/services/support-manual.service';
import { AuthenticationService, MgUser } from 'src/app/core/services/auth.service';
import { SupportPanelComponent } from '../support-panel/support-panel.component';
import { SupportPanelService } from '../support-panel.service';
import {
  loadLauncherPosition, opensBelow, saveLauncherPosition, snapLauncher, SUPPORT_DRAG_THRESHOLD, SupportLauncherPosition,
} from './support-launcher-position';

export const SUPPORT_CREATE_PERMISSION = 'support.tickets.create';
const POLL_INTERVAL_MS = 60_000;
const MAX_BACKOFF_MS = 5 * 60_000;
const POLL_CHECK_MS = 15_000;
const MIN_REFRESH_GAP_MS = 3_000;
const MAX_BADGE = 99;
const NON_STAFF_TYPES: ReadonlyArray<number> = [ROLES.Parent, ROLES.Player, ROLES.Talent];

/**
 * Always-loaded floating support button (FEAT-057 REQ-001, REQ-013). The panel itself is
 * deferred, so only this small component is part of the initial bundle (NFR-004).
 */
@Component({
  selector: 'app-support-launcher',
  standalone: true,
  imports: [TranslateModule, SupportPanelComponent],
  templateUrl: './support-launcher.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Where the user parked the button; the panel reads the same values to open beside it.
  host: {
    '[class.support-side-left]': "position()?.side === 'left'",
    '[class.support-side-right]': "position()?.side === 'right'",
    '[class.support-panel-below]': 'panelBelow()',
    '[style.--support-launcher-bottom.px]': 'position()?.bottom ?? null',
  },
})
export class SupportLauncherComponent implements OnInit {
  private readonly auth = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly api = inject(SupportManualService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  readonly panel = inject(SupportPanelService);

  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');
  private readonly user = toSignal(this.auth.currentUser, { initialValue: this.auth.currentUserValue });

  readonly canUse = computed(() => this.isStaffRequester(this.user()));
  readonly isOpen = this.panel.isOpen;
  readonly unread = this.panel.unreadCount;
  readonly badge = computed(() => (this.unread() > MAX_BADGE ? `${MAX_BADGE}+` : String(this.unread())));

  /** Null keeps the default corner (the end side, away from the menu). */
  readonly position = signal<SupportLauncherPosition | null>(loadLauncherPosition(this.storage()));
  readonly panelBelow = computed(() => {
    const position = this.position();
    return !!position && opensBelow(position, this.document.defaultView?.innerHeight ?? 0, this.fabSize());
  });
  /** The button follows the pointer while it is dragged. */
  readonly dragPoint = signal<{ x: number; y: number } | null>(null);
  private drag: { pointerId: number; startX: number; startY: number; moved: boolean } | null = null;
  private swallowClick = false;

  private readonly canUse$ = toObservable(this.canUse);
  private lastPollAt = 0;
  private pollIntervalMs = POLL_INTERVAL_MS;
  private handledDeepLink: string | null = null;

  constructor() {
    // Return focus to the launcher when the panel closes (NFR-006).
    let wasOpen = false;
    effect(() => {
      const open = this.isOpen();
      if (wasOpen && !open) {
        this.trigger()?.nativeElement.focus();
      }
      wasOpen = open;
    });

    // Losing access (logout, permission removed) clears every support state.
    let couldUse = false;
    effect(() => {
      const can = this.canUse();
      if (couldUse && !can) {
        untracked(() => this.panel.reset());
      }
      couldUse = can;
    });
  }

  ngOnInit(): void {
    // The subscription-expired page renders outside the shell; make sure the user is known.
    this.auth.identity().pipe(take(1), catchError(() => EMPTY), takeUntilDestroyed(this.destroyRef)).subscribe();

    const navigation$ = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));
    merge(of(null), navigation$, this.canUse$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.openFromDeepLink());

    // Timers and visibility events stay outside the Angular zone; only the badge update re-enters.
    this.zone.runOutsideAngular(() => this.startUnreadPolling(navigation$));
  }

  toggle(): void {
    // The click that ends a drag must not also open or close the panel.
    if (this.swallowClick) {
      this.swallowClick = false;
      return;
    }
    this.panel.toggle();
  }

  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    this.drag = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, moved: false };
  }

  onPointerMove(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    if (!drag.moved && Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < SUPPORT_DRAG_THRESHOLD) {
      return;
    }
    if (!drag.moved) {
      drag.moved = true;
      (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    }
    this.dragPoint.set({ x: event.clientX, y: event.clientY });
  }

  onPointerUp(event: PointerEvent): void {
    if (this.drag?.pointerId === event.pointerId) {
      this.finishDrag(event.clientX, event.clientY);
    }
  }

  onPointerCancel(): void {
    this.drag = null;
    this.dragPoint.set(null);
  }

  /** The browser can drop the capture without a pointerup; end the drag where it last was. */
  onLostPointerCapture(): void {
    const point = this.dragPoint();
    if (point) {
      this.finishDrag(point.x, point.y);
    }
  }

  private finishDrag(x: number, y: number): void {
    const drag = this.drag;
    this.drag = null;
    if (!drag?.moved) {
      return;
    }
    const view = this.document.defaultView;
    const position = snapLauncher(
      x,
      y,
      { width: view?.innerWidth ?? 0, height: view?.innerHeight ?? 0 },
      this.fabSize(),
      this.edgeGap(),
    );
    this.dragPoint.set(null);
    this.position.set(position);
    saveLauncherPosition(this.storage(), position);
    this.swallowClick = true;
  }

  private startUnreadPolling(navigation$: Observable<unknown>): void {
    const isVisible = () => this.document.visibilityState === 'visible';
    const isDue = () => isVisible() && Date.now() - this.lastPollAt >= this.pollIntervalMs;
    const isBackingOff = () => this.pollIntervalMs > POLL_INTERVAL_MS;

    merge(
      this.canUse$.pipe(filter(Boolean)),
      navigation$.pipe(filter(() => !isBackingOff() || isDue())),
      interval(POLL_CHECK_MS).pipe(filter(isDue)),
      fromEvent(this.document, 'visibilitychange').pipe(filter(isDue)),
      this.panel.unreadRefreshRequests$,
    )
      .pipe(
        filter(() => this.canUse()),
        throttleTime(MIN_REFRESH_GAP_MS, asyncScheduler, { leading: true, trailing: true }),
        // Stamped before the call so a failing request cannot be retried on every check.
        tap(() => (this.lastPollAt = Date.now())),
        switchMap(() => this.api.unreadCount().pipe(
          tap(() => (this.pollIntervalMs = POLL_INTERVAL_MS)),
          catchError(() => {
            this.pollIntervalMs = Math.min(this.pollIntervalMs * 2, MAX_BACKOFF_MS);
            return EMPTY;
          }),
        )),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((count) => this.zone.run(() => this.panel.setUnreadCount(count)));
  }

  private storage(): Storage | null {
    try {
      return this.document.defaultView?.localStorage ?? null;
    } catch {
      return null;
    }
  }

  private fabSize(): number {
    return this.trigger()?.nativeElement.offsetWidth || 56;
  }

  private edgeGap(): number {
    return parseFloat(getComputedStyle(this.document.documentElement).fontSize || '16') * 1.25;
  }

  /** `?support=SUP-00042` opens the panel on that ticket once per distinct value. */
  private openFromDeepLink(): void {
    if (!this.canUse()) {
      return;
    }
    const ref = this.router.parseUrl(this.router.url).queryParamMap.get('support');
    if (ref && ref !== this.handledDeepLink) {
      this.handledDeepLink = ref;
      this.panel.open(ref);
    }
  }

  /** Academy staff with the permission; parents, players and talents never see it (AC-002). */
  private isStaffRequester(user: MgUser | null): boolean {
    if (!user) {
      return false;
    }
    const types = [...(user.roles || []), Number((user as Record<string, any>)['user_type']?.id ?? NaN)];
    if (types.some((type) => NON_STAFF_TYPES.includes(type))) {
      return false;
    }
    return this.auth.hasPermission(SUPPORT_CREATE_PERMISSION);
  }
}
